import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FIRE_MULTIPLE, INFLATION_CURRENT } from './assumptions/india'
import { BRAND, storageKey, THEME_COLOR } from './brand'
import type { CardModel } from './card/model'
import { cardModelFrom } from './card/model'
import type { CardTheme } from './card/palette'
import { AssumptionsPanel } from './components/AssumptionsPanel'
import { EmailCapture } from './components/EmailCapture'
import { FanChart } from './components/FanChart'
import type { FanLayer } from './components/FanChart'
import { Headline } from './components/Headline'
import { RiskProfilePicker } from './components/RiskProfilePicker'
import { ScenarioBar } from './components/ScenarioBar'
import { ShareSheet } from './components/ShareSheet'
import { Slider } from './components/Slider'
import type { SimInputs, SimResult } from './engine/types'
import { useScenarioState } from './hooks/useScenarioState'
import { useShareCard } from './hooks/useShareCard'
import { useScenarioSimulations } from './hooks/useSimulation'
import type { SimEntry } from './hooks/useSimulation'
import { useThresholdCelebration } from './hooks/useThresholdCelebration'
import { BOUNDS, toMarketModel } from './plan'
import type { Plan } from './plan'
import { activeScenario, comparisonScenario, inputsFor, sharedHorizon } from './scenarios'
import { formatINRCompact, parseINRInUnitOf } from './utils/formatINR'
import { snapMoney } from './utils/sliderScale'

/**
 * Glidepath — the product screen.
 *
 * One screen, chart above, controls in the thumb zone below. Most of the traffic
 * this is built for arrives on a phone, often inside Instagram's in-app browser,
 * and gets exactly one chance: link → move a slider → see their number → share
 * it. Anything that interrupts that path costs more than it can possibly add,
 * which is why there is no onboarding, no modal, no cookie banner, no account,
 * and no Calculate button.
 */
export function App() {
  const { set, updatePlan, compare, stopComparing, focus } = useScenarioState()
  const [interacting, setInteracting] = useState(false)
  const [assumptionsOpen, setAssumptionsOpen] = useState(false)

  const active = activeScenario(set)
  const other = comparisonScenario(set)
  const plan = active.plan

  /*
   * One horizon for the whole set, not one per scenario.
   *
   * Two fans share an x-domain, so they have to run to the same age — otherwise
   * the shorter projection ends in a hard vertical edge partway across the chart,
   * which reads as truncated data rather than as a shorter plan. `scenarios.ts`
   * carries the reasoning and the rendered before/after.
   */
  const horizon = useMemo(() => sharedHorizon(set.scenarios), [set.scenarios])

  /*
   * Per-scenario identity, cached on the plan — NOT a memo over the array.
   *
   * `useScenarioSimulations` decides what to re-run by comparing each entry's
   * `inputs` and `model` by reference, so entry identity is the whole mechanism
   * behind "only the edited scenario re-simulates".
   *
   * A `useMemo` keyed on `set.scenarios` does not give that. `updateActivePlan`
   * preserves the untouched scenario's plan object but returns a new array, so
   * the memo recomputed on every tick and `inputsFor` minted a fresh `inputs`
   * for BOTH scenarios — and both re-simulated on every drag frame. The comment
   * that used to sit here asserted the opposite, which is exactly why nobody
   * measured it.
   *
   * Keying on `scenario.plan` is what actually holds: the untouched scenario's
   * plan is referentially identical across the edit, so its entry is reused and
   * the hook skips it. `horizon` is in the key because it genuinely changes
   * `projectToAge` for both scenarios when either target moves.
   */
  const entryCacheRef = useRef(new Map<string, { plan: Plan; horizon: number; entry: SimEntry }>())
  const entries = useMemo<SimEntry[]>(() => {
    const cache = entryCacheRef.current
    const live = new Set(set.scenarios.map((s) => s.uid))
    for (const key of [...cache.keys()]) if (!live.has(key)) cache.delete(key)

    return set.scenarios.map((scenario) => {
      const hit = cache.get(scenario.uid)
      if (hit !== undefined && hit.plan === scenario.plan && hit.horizon === horizon) {
        return hit.entry
      }
      const entry: SimEntry = {
        // uid, not id: `id` is a reusable display slot, and a returning slot
        // would be served the previous occupant's result.
        key: scenario.uid,
        inputs: inputsFor(scenario, horizon),
        model: toMarketModel(scenario.plan),
      }
      cache.set(scenario.uid, { plan: scenario.plan, horizon, entry })
      return entry
    })
  }, [set.scenarios, horizon])

  const states = useScenarioSimulations(entries, { interacting })
  const activeState = states[active.uid]
  const result = activeState?.result ?? null
  const isPreview = activeState?.isPreview ?? true
  const error = activeState?.error ?? null
  const otherResult = other === null ? null : (states[other.uid]?.result ?? null)

  const inputs = useMemo(() => inputsFor(active, horizon), [active, horizon])

  /* Keyed on the scenario, so switching between two plans re-baselines instead of
     reading the jump in probability as an achievement. */
  const celebrating = useThresholdCelebration(
    result?.probabilityAtTarget ?? 0,
    undefined,
    active.uid,
  )

  const layers = useMemo<FanLayer[]>(
    () =>
      set.scenarios.map((scenario, index) => ({
        id: scenario.id,
        result: states[scenario.uid]?.result ?? null,
        hueToken: scenario.hueToken,
        /*
         * The CLAMPED age the projection was actually run for, not the raw
         * slider value. `clampPlan` forces `fireByAge` past `currentAge`, and
         * the two sliders have no cross-constraint — dragging "Retire by" to 25
         * and "Your age" to 60 left the rule labelled "age 25" beside a headline
         * reading 61, the screen reader hearing 25 over a probability computed
         * for 61, and the URL already storing 61. Four surfaces, three ages.
         */
        targetAge: entries[index].inputs.fireByAge,
        active: scenario.id === set.activeId,
        label: scenario.label,
      })),
    [set.scenarios, set.activeId, states, entries],
  )

  /*
   * The share card is always drawn from the last SETTLED result, never from
   * whatever is on screen this instant.
   *
   * Two things make the on-screen state the wrong source. While a slider is being
   * dragged the worker runs the 2,000-path preview tier, so `probabilityAtTarget`
   * is a couple of points away from where it lands; and the headline's own colour
   * is driven by a rolling value that eases toward the answer over ~140ms. A card
   * rendered mid-gesture would stamp a number the app is about to correct.
   *
   * Holding the old result is safe: every worker run allocates fresh typed arrays
   * and transfers them, so nothing here aliases a buffer the worker still owns.
   */
  const settledRef = useRef<SimResult | null>(null)
  /**
   * The inputs the settled result was computed FOR, by reference.
   *
   * `inputs` is memoised on `plan`, so identity is exactly "same plan". Storing
   * it alongside the result is what makes staleness detectable at all.
   */
  const settledInputsRef = useRef<SimInputs | null>(null)
  const [settledFor, setSettledFor] = useState<SimInputs | null>(null)
  useEffect(() => {
    if (result !== null && !isPreview) {
      settledRef.current = result
      settledInputsRef.current = inputs
      setSettledFor(inputs)
    }
  }, [result, isPreview, inputs])

  /*
   * Not "have we ever settled" — "is what we settled the plan on screen".
   *
   * Releasing a slider posts a fresh full-tier request, and settledRef holds the
   * OLD plan's result until the worker answers. Two things make that window
   * wider than it looks: `isPreview` is false during it too, because `result` is
   * still the previous FULL result, so gating on `isPreview` alone does not
   * close it. Comparing the settled inputs against the current ones does, and it
   * is exact rather than a timing guess.
   *
   * Without this, a card tapped straight after a drag is drawn from the previous
   * plan while the sliders show the new one — internally consistent, and
   * contradicting the screen it was shared from, which is precisely what the
   * comment below claims is unreachable.
   */
  const cardReady = settledFor === inputs

  /*
   * Built when the tap happens, never during render — and note what is NOT
   * passed in. The target age comes out of the result itself, and the theme is
   * read here rather than in the render body.
   *
   * Both were bugs. A ref read during render is a render behind, so the card was
   * drawn from the previous settled run; `inputs.fireByAge` is live while the ref
   * is not, so the eyebrow could name one age over a probability computed for
   * another; and `ThemeToggle` keeps the theme in its own state, so App does not
   * re-render on a toggle and the captured theme went stale. Reading everything
   * at the moment of the gesture makes all three unreachable.
   */
  const buildCardModel = useCallback((): CardModel | null => {
    const settled = settledRef.current
    if (settled === null) return null
    const theme: CardTheme =
      document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
    return cardModelFrom(settled, theme)
  }, [])

  const shareCard = useShareCard(buildCardModel)

  return (
    /*
     * One column on a phone, two from 640px up.
     *
     * Mobile-first is not mobile-only, and reading it that way was a mistake
     * worth naming: the launch goes to r/FIREindia, Reddit is browsed heavily on
     * desktop, and the build-in-public screenshots get taken on a laptop. A
     * 512px column stranded in a 1440px window reads as an unfinished phone app
     * — precisely the "blog-widget calculator" impression the whole design bar
     * exists to avoid.
     *
     * `sm:` is a WIDTH breakpoint, so a phone in landscape (932x430 on a Pro Max)
     * gets the two-column grid. That is the right layout for a wide, short
     * viewport — but the chart height has to be free to shrink there, which is
     * why the clamp floor below is low and expressed in dvh rather than vh.
     */
    <div
      /*
       * `accent-scope` owns `--c-accent`, and everything the user OPERATES reads
       * it: slider tracks and thumb glows, the selected risk chip, focus rings,
       * the share action. While two plans are on screen it points at the hue of
       * the one being edited, so the control column belongs to the plan the
       * sliders write to.
       *
       * F4 shipped without this and the split was obvious the moment you looked:
       * selecting Plan B turned the chip and the fan purple and left every
       * slider, the risk chips and the focus ring in scenario A's teal — the
       * accent was hardcoded to the CHART's median colour, which had been the
       * same thing while there was only ever one fan.
       *
       * Data encodings deliberately do not follow it. The allocation bar stays an
       * ordinal asset ramp and gold still means the goal.
       */
      className="accent-scope mx-auto flex min-h-[100dvh] w-full max-w-lg flex-col px-4 pt-safe pb-safe sm:max-w-5xl sm:px-6"
      style={
        {
          '--c-accent': other === null ? 'var(--c-fan-median)' : `var(${active.hueToken})`,
        } as React.CSSProperties
      }
    >
      <TopBar />

      <div className="flex flex-1 flex-col gap-4 sm:grid sm:grid-cols-[1.35fr_1fr] sm:items-center sm:gap-10">
        {/* The result. On a phone this sits above the fold so the headline is
            never something you have to scroll to find. */}
        {/* flex-1 keeps the controls flush to the bottom of a phone screen,
            within thumb reach. It is inert inside the sm: grid, where the track
            sizing takes over. */}
        <main className="flex flex-1 flex-col justify-center gap-3 py-2">
          {error !== null ? (
            <p
              role="alert"
              className="rounded-[var(--radius-md)] border border-[color:var(--c-negative)] px-4 py-3 text-[length:var(--text-label)] text-[color:var(--c-negative)]"
            >
              The projection failed to run. {error}
            </p>
          ) : (
            <>
              <Headline
                result={result}
                targetAge={inputs.fireByAge}
                /*
                  The pre-tax requirement, recovered from what the engine returned
                  rather than recomputed: `inputs.fireMultiple` is
                  `FIRE_MULTIPLE.value` times the withdrawal gross-up, so dividing
                  it back out is exact.

                  KNOWN LIMITATION, found in review and deliberately not fixed here.
                  `result` is whatever the worker last delivered, while `inputs` is
                  recomputed synchronously every render — so mid-drag this divides a
                  PREVIOUS gross-up's requirement by the CURRENT gross-up. Dragging
                  the target age far enough can understate the "before tax" figure by
                  several percent for the frames before the new result lands; it is
                  transient and self-corrects. The clean fix is for `useSimulation`
                  to publish the multiple it ran with alongside the result, which is
                  a file outside this cycle's agreed scope — so it is reported at
                  SHOW rather than widened into here.
                */
                naiveRequiredAtTarget={
                  result === null
                    ? 0
                    : (result.requiredAtTarget * FIRE_MULTIPLE.value) / inputs.fireMultiple
                }
                comparison={
                  other === null
                    ? null
                    : {
                        activeLabel: `Plan ${active.label}`,
                        activeTargetAge: inputs.fireByAge,
                        otherLabel: `Plan ${other.label}`,
                        otherHue: other.hueToken,
                        otherTargetAge: inputsFor(other, horizon).fireByAge,
                        other: otherResult,
                      }
                }
              />
              {/*
                Height lives in CSS so the chart grows continuously with the
                viewport instead of at two hardcoded breakpoints. The fan is the
                centrepiece and the thing meant to be screenshot-worthy — on a
                phone it shares the screen with the controls, but on a laptop
                there is no reason for it to stay letterbox-thin.

                The floor is 180px, not 320px. A 320px floor looked fine on a
                desktop and broke landscape phones: at 932x430 the 48dvh term
                resolves to ~206px, the floor overrode it, and a 320px chart ate
                74% of the viewport before the headline and controls were drawn —
                so the floor was the only term that ever applied on exactly the
                devices that needed it least.

                dvh, not vh, to match the root's min-h-[100dvh] — mobile browsers
                resolve the two differently while the toolbar is showing.
              */}
              <div className="h-[240px] sm:h-[clamp(180px,44dvh,460px)]">
                <FanChart layers={layers} isPreview={isPreview} celebrating={celebrating} />
              </div>

              {/* Sits with the thing being shared, not in a toolbar somewhere. */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={shareCard.open}
                  disabled={!cardReady}
                  className="rounded-[var(--radius-full)] border border-[color:var(--c-border)] px-4 text-[length:var(--text-label)] text-[color:var(--c-text-secondary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)] disabled:opacity-40"
                  style={{ minHeight: 'var(--touch-target)' }}
                >
                  Share this
                </button>
              </div>
            </>
          )}
        </main>

        {/* Controls. On a phone these sit in the bottom half, within thumb reach
            one-handed; on desktop they become the right column. */}
        <section aria-label="Your plan" className="flex flex-col gap-4 pb-2">
          {/* Above the sliders, because it decides which plan they write to.
              Below it on a phone, everything else is unchanged from F1. */}
          <ScenarioBar
            scenarios={set.scenarios}
            activeId={set.activeId}
            onFocus={focus}
            onCompare={compare}
            onStopComparing={stopComparing}
          />

          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Slider
              label="Your age"
              value={plan.currentAge}
              min={BOUNDS.currentAge.min}
              max={BOUNDS.currentAge.max}
              onChange={(currentAge) => updatePlan({ currentAge })}
              onInteractingChange={setInteracting}
            />
            <Slider
              label="Retire by"
              value={plan.fireByAge}
              min={BOUNDS.fireByAge.min}
              max={BOUNDS.fireByAge.max}
              onChange={(fireByAge) => updatePlan({ fireByAge })}
              onInteractingChange={setInteracting}
            />
          </div>

          <Slider
            label="Invested so far"
            value={plan.currentCorpus}
            min={BOUNDS.currentCorpus.min}
            max={BOUNDS.currentCorpus.max}
            scale={BOUNDS.currentCorpus.scale}
            snap={snapMoney}
            format={formatINRCompact}
            parse={(raw) =>
              parseINRInUnitOf(
                raw,
                plan.currentCorpus,
                BOUNDS.currentCorpus.min,
                BOUNDS.currentCorpus.max,
              )
            }
            onChange={(currentCorpus) => updatePlan({ currentCorpus })}
            onInteractingChange={setInteracting}
          />

          <Slider
            label="Investing monthly"
            value={plan.monthlyInvestment}
            min={BOUNDS.monthlyInvestment.min}
            max={BOUNDS.monthlyInvestment.max}
            scale={BOUNDS.monthlyInvestment.scale}
            snap={snapMoney}
            format={formatINRCompact}
            parse={(raw) =>
              parseINRInUnitOf(
                raw,
                plan.monthlyInvestment,
                BOUNDS.monthlyInvestment.min,
                BOUNDS.monthlyInvestment.max,
              )
            }
            onChange={(monthlyInvestment) => updatePlan({ monthlyInvestment })}
            onInteractingChange={setInteracting}
          />

          <Slider
            label="Spending a year, today"
            value={plan.annualExpensesToday}
            min={BOUNDS.annualExpensesToday.min}
            max={BOUNDS.annualExpensesToday.max}
            scale={BOUNDS.annualExpensesToday.scale}
            snap={snapMoney}
            format={formatINRCompact}
            parse={(raw) =>
              parseINRInUnitOf(
                raw,
                plan.annualExpensesToday,
                BOUNDS.annualExpensesToday.min,
                BOUNDS.annualExpensesToday.max,
              )
            }
            onChange={(annualExpensesToday) => updatePlan({ annualExpensesToday })}
            onInteractingChange={setInteracting}
            /*
              The multiple stopped being a round 30 when F7a priced withdrawal tax
              into it, and interpolating it raw printed "32.074498896...x". Quoted
              to one decimal, with the norm it is built from named beside it so the
              30x convention is still legible rather than silently replaced.
            */
            hint={`Your FIRE number is ${inputs.fireMultiple.toFixed(1)}x this (${FIRE_MULTIPLE.value}x plus withdrawal tax), inflated to each year.`}
          />

          {/*
            Inflation is the only control here that is a belief rather than a
            fact about the user, and it is the highest-leverage number in the
            model — over 40 years it swings the FIRE target by more than 2x.
            Handing it over, with today's actual print marked on the track,
            answers "your inflation assumption is wrong" instead of arguing
            with it.
          */}
          <Slider
            label="Inflation, long run"
            value={plan.inflationMean}
            min={BOUNDS.inflationMean.min}
            max={BOUNDS.inflationMean.max}
            step={0.001}
            format={formatRate}
            marker={{
              value: INFLATION_CURRENT.value,
              label: `now ${formatRate(INFLATION_CURRENT.value)}`,
            }}
            onChange={(inflationMean) => updatePlan({ inflationMean })}
            onInteractingChange={setInteracting}
            hint="RBI targets 4%, tolerating 2–6%. Above that is a bet it misses."
          />

          <RiskProfilePicker
            profileId={plan.profileId}
            onChange={(profileId) => updatePlan({ profileId })}
          />
        </section>
      </div>

      <EmailCapture />

      <Footer onOpenAssumptions={() => setAssumptionsOpen(true)} />

      <AssumptionsPanel open={assumptionsOpen} onClose={() => setAssumptionsOpen(false)} />

      <ShareSheet
        open={shareCard.isOpen}
        status={shareCard.status}
        previews={shareCard.previews}
        error={shareCard.error}
        altText={shareCard.altText}
        notice={shareCard.notice}
        onPick={shareCard.share}
        onSave={shareCard.save}
        onClose={shareCard.close}
      />
    </div>
  )
}

/** One decimal: 5% reads as "5.0%", and 4.38% must not round to "4%". */
function formatRate(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`
}

function TopBar() {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="numeral-hero text-[length:var(--text-title)] text-[color:var(--c-text-primary)]">
        {BRAND.name}
      </span>
      <ThemeToggle />
    </div>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () =>
      (document.documentElement.getAttribute('data-theme') as 'dark' | 'light') ?? 'dark',
  )

  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    document.documentElement.setAttribute('data-theme', next)
    // Keep the browser chrome with the page; the meta tag's own media query
    // cannot follow a user-selected theme.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_COLOR[next])
    try {
      localStorage.setItem(storageKey('theme'), next)
    } catch {
      // Private mode, blocked cookies, or a sandboxed in-app browser. The theme
      // still applies for this session; it just will not persist.
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
      className="grid place-items-center rounded-[var(--radius-full)] text-[color:var(--c-text-tertiary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)]"
      style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  )
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v2.5M12 19.5V22M22 12h-2.5M4.5 12H2M19.07 4.93l-1.77 1.77M6.7 17.3l-1.77 1.77M19.07 19.07l-1.77-1.77M6.7 6.7L4.93 4.93"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Footer({ onOpenAssumptions }: { onOpenAssumptions: () => void }) {
  return (
    <footer className="border-t border-[color:var(--c-border-subtle)] py-3 text-center">
      <p className="text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
        {/*
          The privacy line is first because it is the strongest thing this
          product can say and the cheapest for a competitor to fail to copy — and
          the only reason it is worth anything is that it is checkable.

          As shipped today it is exactly true, and the shape of that changed at
          F6a. There is now exactly ONE network call in src/ — src/subscribe.ts,
          posting an email address to a relative /api path — and nothing else:
          no XMLHttpRequest, sendBeacon, WebSocket or EventSource anywhere, the
          worker has no network import, fonts are self-hosted via @fontsource,
          and the analytics beacon in index.html is commented out.

          "Zero bytes leave" was the old wording and F6a ended it. The sentence
          below survived unchanged for a reason worth stating rather than
          assuming: src/subscribe.ts imports nothing, so its import graph cannot
          reach a Plan, and the endpoint refuses any field beyond the address and
          the consent flag. Both are tests, not intentions. So AMOUNTS still
          never leave, which is what the sentence claims; bytes now do, which it
          never claimed.

          "unless you share it" is not hedging — it is the accurate part. The plan
          is encoded into the URL fragment (see useScenarioState), so copying the
          address bar into WhatsApp does send someone your age, corpus, SIP and
          expenses, in a form the recipient can decode. That is the intended
          feature, but an unqualified "never leave it" sitting beside a share
          affordance that carries the numbers is the kind of absolute claim CCPA
          substantiation is about. Say the true thing instead.

          If the analytics beacon is ever enabled, re-read this sentence first.

          There are now TWO ways numbers can leave, and the sentence has to name
          both or it is not the checkable claim it is trading on. The link carries
          the plan in its fragment. The share card carries less by design —
          no rupee figures at all, enforced by the shape of `CardModel` and by a
          test — but it does carry the probability and the ages, both visibly on
          the image and in the text handed to the OS share sheet. "Your amounts"
          is the accurate scope of what stays put, not "your numbers".

          F4 changed the fragment, so this sentence was re-read against it. "The
          whole plan" was true of v2 and is not true of v3: a compared link
          carries BOTH plans, so someone who builds a private second scenario and
          shares the link has shared that too. "Every plan on screen" is the
          accurate scope, and it is deliberately phrased so it stays true whether
          there is one or two. The share card is unchanged — it renders the active
          scenario and still carries no rupee figures.
        */}
        Every projection is calculated on your device. Your amounts never leave it — a
        shared card shows your odds and ages, never your money, and your link carries
        every plan on screen.
      </p>
      {/*
        Written against the compliance triage, not from a template. Three things
        it has to do, each traceable to a specific rule:

        1. Not hold out as an investment adviser. SEBI Reg 3(1) makes "holding
           itself out" a limb separate from actually advising, so the copy —
           here and in the launch post — is part of the exposure surface, not
           just the software.
        2. Say the assumptions are estimates. The CCPA Guidelines, 2022 require
           claims to be capable of substantiation; a probability computed from
           unsourced return assumptions is a claim whose substantiation does not
           exist yet.
        3. Point at a registered adviser for the decision itself.

        The 30x multiple is named explicitly, and that is deliberate. An earlier
        draft enumerated only "return and inflation assumptions", which implicitly
        warranted the one number it left out — and the multiple is both the most
        prominent figure on screen (the slider hint states it as bare fact) and
        the least derived. Its own dossier note concedes it is "a US finding with
        an Indian safety margin bolted on". An enumerated disclaimer that omits
        the load-bearing assumption is worse than a general one.

        The short qualifier under the headline carries the prominence
        requirement; this carries the detail.
      */}
      <p className="mt-1.5 text-[length:var(--text-micro)] leading-relaxed text-[color:var(--c-text-muted)]">
        A free educational calculator, not investment advice. Not registered with SEBI as an
        investment adviser or research analyst. The return, inflation and{' '}
        {FIRE_MULTIPLE.value}x-expenses assumptions are estimates and conventions, not
        predictions, and every projection here is a model output rather than a promise. The
        capital-gains rates are statutory, but the withdrawal tax applied here is an
        estimate for a model portfolio at a model holding period — not a calculation of
        what you would actually pay, which depends on your own holdings and what you paid
        for them. Speak to a SEBI-registered adviser before acting on any of it.
      </p>
      <button
        type="button"
        onClick={onOpenAssumptions}
        className="mt-1.5 rounded-[var(--radius-sm)] px-2 py-1 text-[length:var(--text-micro)] text-[color:var(--c-accent)] underline underline-offset-2 transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)]"
      >
        Why these numbers?
      </button>
    </footer>
  )
}
